import { useLocation } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useStores } from '@/hooks/useStores';
import { useT } from '@/i18n/LocaleProvider';
import { lazyOverlay } from './lazyRoute';
import { PublicWorkAreaGate } from '@/features/public-read/PublicWorkAreaGate';
import { NoPageHere } from './NoPageHere';
import { RouteRedirect } from './RouteRedirect';
import { quotedMissingReference } from './workAreaRoute';
import type { WorkAreaRouteStateType } from './workAreaRouteState';

/*
  What an address shows before it can show a page: its new address, waiting for the list, naming
  nothing, a namespace, or naming several. Drawn by `AddressState` for `WorkAreaRouteBoundary`,
  `NamespaceAddressPage` and `LegacyAddressRedirect`, which answer the same question for a work
  area's address, a namespace's and one from before R-5.
*/

/**
 * Every state `workAreaRouteState` decides that is not a page, drawn one way wherever the router
 * landed, so the three routes that can meet one cannot draw it three ways.
 */
export const AddressState = observer(function AddressState({
  state,
}: {
  state: Exclude<WorkAreaRouteStateType, { kind: 'page' }>;
}) {
  const location = useLocation();
  const { projectStore } = useStores();
  switch (state.kind) {
    case 'redirect':
      // Replaced in history, so Back never returns to it; the query, the fragment and the
      // navigation state travel with it. Said as still coming while the new address's chunk is
      // read, or a launch at an old address faded over nothing (I-2059).
      return (
        <RouteRedirect
          to={`${state.to}${location.search}${location.hash}`}
          state={location.state}
        />
      );
    case 'no-page':
      return <NoPageHere address={location.pathname} workArea={state.workArea} />;
    case 'waiting':
      // No answer yet, or none at all: the list is loading, or its first load failed.
      return (
        <WorkAreaRouteWaiting
          failed={Boolean(projectStore.error) && !projectStore.loading}
          onRetry={() => void projectStore.fetchProjects()}
        />
      );
    case 'namespace':
      return <NamespaceWorkAreas namespace={state.namespace} workAreas={state.workAreas} />;
    case 'no-work-area':
      // F-0338 AC-PV21: a work area the reader is not a member of may still be Public.
      return (
        <PublicWorkAreaGate
          signedIn
          signedInAction={(workArea) => <PublicJoinOffer workArea={workArea} />}
          fallback={
            <WorkAreaRouteNotFound
              reference={quotedMissingReference(state.reference, projectStore.projects) ?? ''}
            />
          }
        />
      );
    case 'ambiguous':
      return (
        <WorkAreaRouteChoice
          reference={state.reference}
          candidates={state.candidates}
          route={state.route}
          search={`${location.search}${location.hash}`}
        />
      );
  }
});

/** While the work-area list has not answered: nothing that could act on a work area is mounted. */
export function WorkAreaRouteWaiting({
  failed,
  onRetry,
}: {
  failed: boolean;
  onRetry: () => void;
}) {
  const t = useT();
  // A section inside the layout's main landmark, never a second one (I-0703).
  return (
    <section aria-labelledby="work-area-route-waiting" className="flex-1 overflow-y-auto px-5 py-8">
      <div className="mx-auto max-w-xl">
        {failed ? (
          <WorkAreaRouteFailed onRetry={onRetry} />
        ) : (
          // `data-route-pending`, as every route placeholder carries: until the list answers, no
          // screen is here yet. Without it the app shell announced the screen, and `awaitView`
          // returned, while this was still showing (gate 182).
          <h1
            id="work-area-route-waiting"
            aria-busy="true"
            data-route-pending=""
            className="text-[13px] font-normal text-gray-500 dark:text-gray-400"
          >
            {t('navigation.workAreaRoute.opening')}
          </h1>
        )}
      </div>
    </section>
  );
}

const rareStates = () => import('./WorkAreaRouteRareStates');

/** The list's first load failed (`WorkAreaRouteRareStates.tsx`, its own chunk). */
const WorkAreaRouteFailed = lazyOverlay(rareStates, (module) => module.WorkAreaRouteFailed);

/** F-0739 (PJ-7): Join, or whom to ask, in a Public work area the reader is not a member of. */
const PublicJoinOffer = lazyOverlay(
  () => import('@/features/examples/PublicJoinOffer'),
  (module) => module.PublicJoinOffer,
);

/** An address that names no work area the reader can open. Never a fallback to another one. */
export const WorkAreaRouteNotFound = lazyOverlay(
  rareStates,
  (module) => module.WorkAreaRouteNotFound,
);

/** Every work area an ambiguous address could mean, each linking to the same view in it. */
export const WorkAreaRouteChoice = lazyOverlay(rareStates, (module) => module.WorkAreaRouteChoice);

/** The work areas in one namespace, each linking to its address. */
export const NamespaceWorkAreas = lazyOverlay(rareStates, (module) => module.NamespaceWorkAreas);
